import { RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { ProcurementCatalogModal } from '../components/ProcurementCatalogModal';
import { procurementCatalogsApi } from '../procurementCatalogsApi';
import type {
  ProcurementCatalogDescriptor,
  ProcurementCatalogEntry,
} from '../procurementCatalogTypes';

/**
 * One of the two flat procurement vocabularies — what a PPMP line buys, or how it is procured.
 *
 * **One page for both**, parameterised by the descriptor. They are one shape and one
 * administrative job, so two copies of this file would be two places for a column to be added
 * to one of. See `ProcurementCatalogDescriptor`, and the L&D four it is modelled on.
 */
export function ProcurementCatalogIndexPage({
  catalog,
}: {
  catalog: ProcurementCatalogDescriptor;
}) {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<ProcurementCatalogEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    entry: ProcurementCatalogEntry;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchEntries = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      procurementCatalogsApi.list(
        catalog.slug,
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [catalog.slug],
  );

  const rowActions = useCallback(
    (entry: ProcurementCatalogEntry): MenuItem[] => [
      {
        key: `edit-${entry.id}`,
        label: 'Edit',
        permission: 'procurement-catalogs.update',
        onClick: () => setEditing(entry),
      },
      {
        key: `restore-${entry.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'procurement-catalogs.delete',
        disabledReason: entry.isActive ? `This ${catalog.noun} is already active.` : null,
        onClick: () => setConfirming({ kind: 'activate', entry }),
      },
    ],
    [catalog.noun],
  );

  // Rebuilt per catalog because the anchor's header is the vocabulary's own word. Memoised so
  // the shell's column resolution is not handed a new array on every render.
  const columns = useMemo(() => buildColumns(catalog), [catalog]);

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await procurementCatalogsApi.remove(catalog.slug, confirming.entry.id);
      } else {
        await procurementCatalogsApi.restore(catalog.slug, confirming.entry.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<ProcurementCatalogEntry>
        /*
          ONE module key for both vocabularies, which is what the settings cog's History Logs
          and Clear data are keyed on — maintaining the two is one administrative job.
        */
        module="procurement-catalogs"
        /*
          The shell derives its noun from the module key, and that is wrong here for the reason
          it is wrong on the L&D four: two vocabularies share the key, so "Clear all procurement
          catalogs" would misstate the scope of the widest destructive action the page has. It
          clears THIS list.
        */
        moduleNoun={catalog.nounPlural}
        // Reached through Procurement Planning → Settings, so that crumb is the way back.
        breadcrumb={[
          'Procurement',
          'Procurement Planning',
          { label: 'Settings', to: '/procurement/procurement-planning/settings' },
          catalog.label,
        ]}
        columns={columns}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={[ENTITY_TYPES[catalog.slug]]}
        rowKey={(entry) => entry.id}
        fetch={fetchEntries}
        reloadKey={reloadKey}
        searchPlaceholder={`Search ${catalog.nounPlural}`}
        emptyMessage={catalog.emptyMessage}
        onCreate={() => setCreating(true)}
        createPermission="procurement-catalogs.create"
        // The anchor opens the PROFILE; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(entry) => navigate(`${catalog.settingsPath}/${entry.id}`)}
        openPermission="procurement-catalogs.read"
        rowActions={rowActions}
        onDelete={(entry) => setConfirming({ kind: 'deactivate', entry })}
        deletePermission="procurement-catalogs.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(entry) =>
          entry.isActive ? null : `This ${catalog.noun} is already deleted.`
        }
        // Clears THIS vocabulary only — the entry sits on this page and clears what it lists.
        onClearData={() => procurementCatalogsApi.clear(catalog.slug)}
        clearDataPermission="procurement-catalogs.clear-data"
        otherInfo={(entry) => entry}
      />

      {creating || editing !== null ? (
        <ProcurementCatalogModal
          catalog={catalog}
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'deactivate' ? `Delete ${catalog.noun}` : `Restore ${catalog.noun}`
          }
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`procurement-catalog-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.entry.name}" stops being offered on a procurement line.`
                  : `"${confirming.entry.name}" is offered again on a procurement line.`}
              </p>

              {/*
                THE COUNT BEFORE THE TICK, because the delete does less than its label suggests:
                a line already filed under this row snapshotted the word and keeps reading
                exactly as it did, so a reader who expected those lines to change would
                otherwise see nothing happen.
              */}
              {confirming.kind === 'deactivate' && confirming.entry.lineItemCount > 0 ? (
                <p>
                  {confirming.entry.lineItemCount} procurement line
                  {confirming.entry.lineItemCount === 1 ? '' : 's'}
                  {confirming.entry.lineItemCount === 1 ? ' is' : ' are'} already filed under it.
                  Those lines keep the name and still state it — retiring a {catalog.noun} does
                  not rewrite what a plan says it was procuring.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted {catalog.noun} can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * The audit entity behind each slug.
 *
 * Two tables, two CLR names, so History Logs on one vocabulary's page shows that vocabulary's
 * trail rather than both — the one place the two genuinely are separate records.
 */
const ENTITY_TYPES: Record<ProcurementCatalogDescriptor['slug'], string> = {
  types: 'ProcurementType',
  modes: 'ModeOfProcurement',
};

function buildColumns(
  catalog: ProcurementCatalogDescriptor,
): ColumnDef<ProcurementCatalogEntry>[] {
  return [
    {
      // The name is the anchor: a vocabulary row is referred to by its word.
      key: 'name',
      header: catalog.columnHeader,
      width: 280,
      sortable: true,
      render: (entry) => <TextCell value={entry.name} />,
    },
    {
      key: 'description',
      header: 'Description',
      width: 420,
      flex: true,
      summary: true,
      render: (entry) => <TextCell value={entry.description} />,
    },
    {
      key: 'lineItemCount',
      header: 'Procurement lines',
      width: 150,
      align: 'right',
      /*
        DELIBERATELY NOT a summary value. The list view concatenates its summary values with no
        labels, so a count arrives as a bare `· 0` at the end of the line — a number a reader
        cannot interpret without the header this column has and that line does not. The
        description is the one value here that reads on its own.
      */
      /*
        NOT sortable, and the server's allow-list omits it deliberately: it is a correlated
        count, so ordering a page by it would make the database compute one per row before it
        could page. The column declares itself unsortable rather than offering a header that
        silently falls back to the default sort.
      */
      render: (entry) => <TextCell value={String(entry.lineItemCount)} mono numeric />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (entry) =>
        entry.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];
}
